import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useI18n } from "@/stores/i18nStore";
import { buildPageSeo, SITE_ORIGIN } from "@/lib/seo";

function upsertJsonLd(id: string, value: Record<string, unknown>) {
  let script = document.getElementById(id) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(value);
}

const titleCase = (value: string) =>
  decodeURIComponent(value)
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

function breadcrumbSchema(pathname: string, locale: string) {
  const path = pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
  const parts = path.split("/").filter(Boolean);
  const home = locale === "fr" ? "Accueil" : "Home";
  const labels = locale === "fr"
    ? { catalog: "Catalogue", category: "Catégorie", collection: "Collection", product: "Produit" }
    : { catalog: "Catalog", category: "Category", collection: "Collection", product: "Product" };

  const items = [
    { "@type": "ListItem", position: 1, name: home, item: `${SITE_ORIGIN}/` },
  ];
  if (path !== "/") {
    const section = parts[0] as keyof typeof labels;
    const name = parts[1] ? titleCase(parts[1]) : labels[section] || titleCase(parts[0]);
    items.push({
      "@type": "ListItem",
      position: 2,
      name: parts[1] ? `${labels[section] || titleCase(parts[0])} — ${name}` : name,
      item: `${SITE_ORIGIN}${path}`,
    });
  }

  return { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: items };
}

function upsertMeta(selector: string, attribute: "name" | "property", key: string, content: string) {
  let meta = document.querySelector(selector) as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute(attribute, key);
    document.head.appendChild(meta);
  }
  meta.content = content;
}

export function SeoManager() {
  const { pathname } = useLocation();
  const { locale } = useI18n();
  const { data: settings } = useSiteSettings();

  useEffect(() => {
    const seo = buildPageSeo(pathname, locale, settings?.seo);

    document.title = seo.title;
    upsertMeta('meta[name="description"]', "name", "description", seo.description);
    upsertMeta('meta[name="robots"]', "name", "robots", seo.robots);
    upsertMeta('meta[property="og:title"]', "property", "og:title", seo.title);
    upsertMeta('meta[property="og:description"]', "property", "og:description", seo.description);
    upsertMeta('meta[property="og:url"]', "property", "og:url", seo.canonical);
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", seo.title);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", seo.description);

    if (seo.ogImage) {
      upsertMeta('meta[property="og:image"]', "property", "og:image", seo.ogImage);
    }

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = seo.canonical;

    upsertJsonLd("skinvault-organization-schema", {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "SkinVault",
      url: SITE_ORIGIN,
      logo: settings?.seo?.ogImage || `${SITE_ORIGIN}/favicon.ico`,
    });
    upsertJsonLd("skinvault-breadcrumb-schema", breadcrumbSchema(pathname, locale));
  }, [locale, pathname, settings?.seo]);

  return null;
}
